/** @format */

import MainChart from 'components/charts/multiline-chart/components/main-chart';
import {CHARTS_MARGIN_BOTTOM, CHARTS_MARGIN_TOP} from 'components/charts/multiline-chart/const';
import {type Chart, type ChartLabelList} from 'components/charts/multiline-chart/model/charts';
import {type ChartTypeSettings} from 'components/charts/multiline-chart/model/common';
import {type TooltipDataList} from 'components/charts/multiline-chart/model/tooltip';

import {AxisLabel, AxisRow, AxisTodayLabel, ChartColumn, PlotWrap, TodayLine} from './styles';

// Height of the X-axis strip below the plot (Figma node 10282:123068 calendar).
const AXIS_HEIGHT = 14;

interface Props<ChartId extends string> {
  chart: Chart<ChartId>;
  tooltipData: TooltipDataList<ChartId>;
  height: number;
  // Period start / end date labels under the chart (X-axis). Omit both to hide the axis.
  axisStart?: string;
  axisEnd?: string;
  // Today's position along the X-axis (0..1) + label, for the vertical today marker. Null → none.
  todayRatio?: number | null;
  todayLabel?: string;
  // Value label(s) drawn at data points (the metric value at today). recharts positions each.
  valueLabels?: ChartLabelList;
  // Value label(s) drawn on the forecast line (the forecast value at period end).
  forecastValueLabels?: ChartLabelList;
  // Plan-execution color (good/bad) for the fact area/line — see getValueColor. Omit (no plan) to
  // keep the chart's own default color.
  color?: string;
}

/**
 * Compact overview-card chart — mirrors the home/main/status «KPI и показатели» widget
 * (pages/Home/Main/Project/components/KpiAndIndicators/components/Widget): a bare MainChart with
 * no MultilineChart x-axis stack / 72px top offset, so the plot fills exactly `height` and never
 * overflows the card. Fact area + dashed plan + dashed forecast tail, compact tooltip, no labels.
 *
 * When `axisStart`/`axisEnd` are given, a 14px X-axis strip with the period start (left) and end
 * (right) labels is rendered below the plot (Figma node 10282:123026); the plot shrinks to fit.
 * When `todayRatio` is set, a vertical today marker overlays the plot and today's date is labelled
 * on the axis at that position (brighter than the muted ends).
 */
export function OverviewChart<ChartId extends string>({
  chart,
  tooltipData,
  height,
  axisStart,
  axisEnd,
  todayRatio,
  todayLabel,
  valueLabels,
  forecastValueLabels,
  color,
}: Props<ChartId>) {
  const showAxis = Boolean(axisStart) || Boolean(axisEnd);
  const plotHeight = showAxis ? Math.max(0, height - AXIS_HEIGHT) : height;
  const hasToday = typeof todayRatio === 'number';
  const todayLeft = hasToday ? `${todayRatio * 100}%` : undefined;

  // Plan-execution color overrides the chart's default (drives the fact area/line + today label);
  // no color → render the chart as-is (no plan to grade against).
  const chartWithColor = color ? {...chart, color} : chart;

  return (
    <ChartColumn>
      <PlotWrap>
        <MainChart<ChartId>
          key={String(chart.chartId)}
          chart={chartWithColor}
          syncId={String(chart.chartId)}
          tooltipData={tooltipData}
          onChartClick={() => {}}
          chartIndex={0}
          chartTypeSettings={{[chart.chartId]: 'primary'} as ChartTypeSettings<ChartId>}
          overrideChartHeight={plotHeight}
          // left/right 0 so a data point at ratio r sits at exactly r·width — the today marker aligns.
          margin={{top: CHARTS_MARGIN_TOP, bottom: CHARTS_MARGIN_BOTTOM, left: 0, right: 0}}
          maxPitLabels={1}
          maxPeakLabels={1}
          isCompactTooltip
          planLineDashed
          valueLabels={valueLabels}
          forecastValueLabels={forecastValueLabels}
        />

        {hasToday && <TodayLine sx={{left: todayLeft}} />}
      </PlotWrap>

      {showAxis && (
        <AxisRow>
          <AxisLabel>{axisStart}</AxisLabel>
          {hasToday && todayLabel && (
            <AxisTodayLabel sx={{left: todayLeft}}>{todayLabel}</AxisTodayLabel>
          )}
          <AxisLabel>{axisEnd}</AxisLabel>
        </AxisRow>
      )}
    </ChartColumn>
  );
}
